import React, { useState } from "react";
import { UploadOutlined } from "@ant-design/icons";
import { Button, message, Modal, Progress, Typography } from "antd";
import { useTranslation } from "react-i18next";

import { type ExerciseRecordingWithExerciseInfo } from "@fitness-app/data-models/entities/ExerciseRecording";
import { getErrorMessage } from "@fitness-app/utils";

import { UploadFileStatus, type StorageConfig, type UploadValidatorConfig } from "~/components/Upload/types";
import UploadInput from "~/components/Upload/UploadInput";
import { UploadFilesProvider, useUploadFilesContext } from "~/components/Upload/UploadProvider";
import { useUpdateExerciseRecording } from "./useUpdateExerciseRecording";

const MAX_VIDEO_SIZE_IN_BYTES = 100 * 1024 * 1024; // 100 MB

const getUploadConfig = (
  traineeId: string,
): { validatorConfig: UploadValidatorConfig; storageConfig: StorageConfig } => ({
  validatorConfig: {
    maxFilesListSize: MAX_VIDEO_SIZE_IN_BYTES,
    maxFilesListLength: 1,
    maxFileSize: MAX_VIDEO_SIZE_IN_BYTES,
    allowedMimeTypes: ["video/mp4", "video/quicktime", "video/x-m4v", "video/webm"],
  },
  storageConfig: {
    isPublicBucket: false,
    bucketName: "users",
    objectPath: `${traineeId}/videos`,
  },
});

interface UploadExerciseVideoModalProps {
  visible: boolean;
  recording: ExerciseRecordingWithExerciseInfo | null;
  traineeId: string;
  onClose: () => void;
}

const UploadExerciseVideoModalContent = ({
  recording,
  onClose,
}: {
  recording: ExerciseRecordingWithExerciseInfo;
  onClose: () => void;
}) => {
  const { t } = useTranslation(["trainees", "common"]);
  const { files, removeFile, resetFilesList } = useUploadFilesContext();
  const { setExerciseRecordingVideo, isUpdating } = useUpdateExerciseRecording();
  const [isSaving, setIsSaving] = useState(false);

  const uploadedFile = files.find((f) => f.status === UploadFileStatus.SUCCESS);
  const uploadingFile = files.find((f) => f.status === UploadFileStatus.UPLOADING);

  const handleSave = async () => {
    if (!uploadedFile?.url) return;

    setIsSaving(true);
    try {
      await setExerciseRecordingVideo(uploadedFile.url, uploadedFile.objectPath, recording);
      resetFilesList();
      onClose();
    } catch (error) {
      message.error(`Błąd podczas zapisywania filmu: ${getErrorMessage(error)}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (uploadedFile) {
      removeFile(uploadedFile);
    }
    resetFilesList();
    onClose();
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-gray-50 p-4">
        <Typography.Text strong>{t("measurements.exerciseName")}:</Typography.Text>
        <Typography.Text className="ml-2">{recording.exercise.name}</Typography.Text>
      </div>

      {!uploadedFile && !uploadingFile && (
        <UploadInput multiple={false}>
          <div className="hover:border-primary-500 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 p-8 transition-colors">
            <UploadOutlined className="mb-2 text-4xl text-gray-400" />
            <Typography.Text className="text-gray-600">{t("measurements.uploadVideoPlaceholder")}</Typography.Text>
            <Typography.Text type="secondary" className="mt-1 text-xs">
              MP4, MOV, M4V, WebM (max 100MB)
            </Typography.Text>
          </div>
        </UploadInput>
      )}

      {uploadingFile && (
        <div className="rounded-lg border border-gray-200 p-4">
          <Typography.Text>{uploadingFile.originalName}</Typography.Text>
          <Progress percent={uploadingFile.progress || 0} status="active" />
        </div>
      )}

      {uploadedFile && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
          <div className="flex items-center justify-between gap-2">
            <Typography.Text className="text-green-700">{uploadedFile.originalName}</Typography.Text>
            <Button size="small" danger onClick={() => removeFile(uploadedFile)}>
              {t("common:button.remove")}
            </Button>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4">
        <Button onClick={handleCancel}>{t("common:button.cancel")}</Button>
        <Button type="primary" onClick={handleSave} disabled={!uploadedFile} loading={isSaving || isUpdating}>
          {t("common:button.add")}
        </Button>
      </div>
    </div>
  );
};

export const UploadExerciseVideoModal = ({
  visible,
  recording,
  traineeId,
  onClose,
}: UploadExerciseVideoModalProps): React.ReactElement | null => {
  const { t } = useTranslation(["trainees", "common"]);

  if (!recording) return null;

  const uploadConfig = getUploadConfig(traineeId);

  return (
    <Modal
      open={visible}
      title={t("measurements.uploadVideoTitle")}
      footer={null}
      onCancel={onClose}
      destroyOnClose
      width={500}
    >
      <UploadFilesProvider {...uploadConfig}>
        <UploadExerciseVideoModalContent recording={recording} onClose={onClose} />
      </UploadFilesProvider>
    </Modal>
  );
};
